/**
 * Variant pricing helpers.
 *
 * Admin-configured variant `priceAdjustment` values are stored in dollars
 * (for example `0.25` or `-0.25`), while product prices are stored in cents.
 */

function parseVariantData(source) {
  if (!source) return null;

  const raw = source.variants_data ?? source.variantsData ?? source;
  if (!raw) return null;

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return typeof raw === 'object' ? raw : null;
}

function normalizeOption(value) {
  return String(value ?? '').trim().toLowerCase();
}

export function getVariantCombination(source, selectedSize = null, selectedColor = null) {
  const variantData = parseVariantData(source);
  const combinations = Array.isArray(variantData?.combinations) ? variantData.combinations : [];

  const size = normalizeOption(selectedSize ?? source?.selectedSize);
  const color = normalizeOption(selectedColor ?? source?.selectedColor);

  if (!size && !color) return null;

  return combinations.find((combo) => {
    const comboSize = normalizeOption(combo?.size);
    const comboColor = normalizeOption(combo?.color);

    if (size && comboSize !== size) return false;
    if (!size && comboSize) return false;
    if (color && comboColor !== color) return false;
    if (!color && comboColor) return false;

    return true;
  }) || null;
}

export function getVariantPriceAdjustmentCents(source, selectedSize = null, selectedColor = null) {
  const combo = getVariantCombination(source, selectedSize, selectedColor);
  const adjustmentDollars = Number(combo?.priceAdjustment ?? 0);
  return Number.isFinite(adjustmentDollars) ? Math.round(adjustmentDollars * 100) : 0;
}

export function getVariantAdjustedPriceCents(source, selectedSize = null, selectedColor = null) {
  const basePrice = Number(source?.price_cents ?? source?.price ?? 0);
  const adjustmentCents = getVariantPriceAdjustmentCents(source, selectedSize, selectedColor);
  return Math.max(0, (Number.isFinite(basePrice) ? basePrice : 0) + adjustmentCents);
}
